import type { Deposit, DepositFiscalStatus } from "../api/deposits";

// Касовият бон на депозита (Бон 1) — едно място за решението „готов ли е
// депозитът" и „какво казва изходът". Ползват го модалът „Депозити",
// изборът в „Закрий", „Издай бон" и „Справка депозити". Състоянията ги пише
// BE (`deposits.fiscal_status`); FE само ги чете — договорът е ред 53 в
// docs/cross-repo-contract.md.

/**
 * Изходът на бона от гледна точка на оператора:
 *  • `ready` — бон има (`printed`) или по закон не трябва (`not_required`):
 *    депозитът плаща сметка и се фактурира;
 *  • `unknown` — бонът може да е на хартия (чака принтера / без отговор):
 *    повторът е със СЪЩИЯ ключ и не печата втори;
 *  • `failed` — принтерът отказа, картата не е теглена: бонът се издава наново;
 *  • `card_charged` — картата Е теглена, бонът не е: повторът довършва бона
 *    без ново теглене — НЕ теглете картата втори път;
 *  • `missing` — заварен депозит отпреди касовия бон (0.0.361): бон още няма.
 */
export type DepositReceiptState =
  | "ready"
  | "unknown"
  | "failed"
  | "card_charged"
  | "missing";

const STATE_BY_STATUS: Record<DepositFiscalStatus, DepositReceiptState> = {
  printed: "ready",
  not_required: "ready",
  pending: "unknown",
  unknown: "unknown",
  failed: "failed",
  card_charged: "card_charged",
};

/** Състоянието на бона по записа на BE; непознат статус — като неясен (по-
 *  безопасното: повторът не печата втори бон). */
export const depositReceiptStateOf = (
  deposit: Pick<Deposit, "fiscalStatus">,
): DepositReceiptState =>
  deposit.fiscalStatus === null || deposit.fiscalStatus === undefined
    ? "missing"
    : (STATE_BY_STATUS[deposit.fiscalStatus] ?? "unknown");

/**
 * Може ли депозитът да плати сметка и да се фактурира. BE праща
 * `fiscalReady`; без него (по-стар отговор) решава статусът — заварен
 * депозит НЕ е готов (договорът: не плаща сметка, докато не му се издаде бон).
 */
export const isDepositFiscalReady = (
  deposit: Pick<Deposit, "fiscalStatus" | "fiscalReady">,
): boolean =>
  typeof deposit.fiscalReady === "boolean"
    ? deposit.fiscalReady
    : depositReceiptStateOf(deposit) === "ready";

/** Неясен изход — „Издай бон" първо пита (бонът може вече да е излязъл). */
export const isDepositReceiptOutcomeUnknown = (
  deposit: Pick<Deposit, "fiscalStatus">,
): boolean => depositReceiptStateOf(deposit) === "unknown";
